import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Alert,
    Autocomplete,
    CircularProgress,
    Paper,
    InputAdornment,
    Collapse,
    Stack,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
} from '@mui/material';
import {
    Business as BusinessIcon,
    Person as PersonIcon,
    NoteAdd as NoteAddIcon,
    History as HistoryIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Inventory as InventoryIcon,
    Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import Brandcrumb from '../../../../componentsUiBlock/Brandcrumb';
import { request } from '../../../../utils/request';
import { toast } from 'react-toastify';
import {
    ProductReturnFormData,
    Status,
    StatusNoteCreate,
    Item,
    ProductReturnParty,
    Product as ProductType,
    StatusActivity,
    ProductReturnVendor
} from '../../types';
import { useFinancialYear } from '../../../../utils/useFinancialYear';

// Status configuration
const STATUS_OPTIONS: Status[] = [
    'RETURN_CREATED',
    'RECEIVED_AT_SHOP',
    'SENT_TO_VENDOR',
    'RECEIVED_BY_VENDOR'
];

const STATUS_CONFIG: Record<Status, { label: string; color: string; icon: string }> = {
    RETURN_CREATED: { label: 'Return Created', color: 'info', icon: '📝' },
    RECEIVED_AT_SHOP: { label: 'Received at Shop', color: 'primary', icon: '🏪' },
    SENT_TO_VENDOR: { label: 'Sent to Vendor', color: 'warning', icon: '📦' },
    RECEIVED_BY_VENDOR: { label: 'Received by Vendor', color: 'info', icon: '🏭' },
};

const CreateEditProductReturn = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;
    const financialYear = useFinancialYear();

    // React Hook Form
    const {
        control,
        handleSubmit,
        watch,
        setValue,
        getValues,
        formState: { errors },
        reset,
    } = useForm<ProductReturnFormData>({
        defaultValues: {
            productReturnNumber: '',
            productReturnDate: new Date(),
            items: [],
            status: 'RETURN_CREATED',
            description: '',
        }
    });

    // Watch status for conditional fields
    const status = watch('status');
    const party = watch('party');
    const vendor = watch('vendor');
    const previousStatus = watch('previous_status');
    const items = watch('items') || [];

    // State
    const [parties, setParties] = useState<ProductReturnParty[]>([]);
    const [vendors, setVendors] = useState<ProductReturnVendor[]>([]);
    const [products, setProducts] = useState<ProductType[]>([]);
    const [, setFilteredProducts] = useState<ProductType[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [, setError] = useState('');
    const [, setSuccess] = useState('');
    const [statusNotes, setStatusNotes] = useState<StatusNoteCreate[]>([]);
    const [showStatusNote, setShowStatusNote] = useState(false);
    const [, setStatusHistory] = useState<StatusActivity[]>([]);
    const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);
    const [selectedProduct, setSelectedProduct] = useState<ProductType | null>(null);
    const [isLoadingProductReturnNumber, setIsLoadingProductReturnNumber] = useState(false);
    const [itemForm, setItemForm] = useState<Partial<Item>>({
        srNo: 1,
        itemName: '',
        aliasName: '',
        hsnNo: '',
        quantity: 1,
        price: 0,
        amount: 0,
        productId: ''
    });

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    // Fetch next product return number
    const fetchNextProductReturnNumber = useCallback(async () => {
        if (isEditMode) return;

        setIsLoadingProductReturnNumber(true);
        try {
            const res = await request({
                url: 'productReturn/get-next-product-return-number',
                method: "get",
            });

            if (res.success && res.productReturnNumber) {
                const newProductReturnNumber = res.productReturnNumber;
                setValue('productReturnNumber', newProductReturnNumber, {
                    shouldValidate: true,
                    shouldDirty: true
                });
            } else {
                // Fallback to financial year based number
                const fallbackNumber = `RR${financialYear}-1`;
                setValue('productReturnNumber', fallbackNumber);
            }
        } catch (error) {
            console.error("Error fetching next return number:", error);
            // Fallback to financial year based number
            const fallbackNumber = `RR${financialYear}-1`;
            setValue('productReturnNumber', fallbackNumber);
        } finally {
            setIsLoadingProductReturnNumber(false);
        }
    }, [isEditMode, setValue, financialYear]);

    // Fetch all required data
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // Fetch parties (customers)
            const partiesResponse = await request({
                url: 'party/get-all-parties',
                method: 'get',
            });
            setParties(partiesResponse.parties || []);

            // Fetch vendors
            const vendorsResponse = await request({
                url: 'vendor/get-all-vendors',
                method: 'get',
            });
            setVendors(vendorsResponse.vendors || []);

            // Fetch products
            const productsResponse = await request({
                url: 'product/get-all-products',
                method: 'get',
            });
            setProducts(productsResponse.products || []);
            setFilteredProducts(productsResponse.products || []);

            if (isEditMode && id) {
                // Fetch single product return data for edit
                const returnResponse = await request({
                    url: `/productReturn/get-single-product-return/${id}`,
                    method: 'get',
                });

                const returnData: any = returnResponse.data || returnResponse;

                if (!returnData) {
                    throw new Error('Product return data not found');
                }

                // Fetch status history if exists
                if (returnData.status_history) {
                    setStatusHistory(returnData.status_history);
                }

                if (returnData.status_notes) {
                    setStatusNotes(returnData.status_notes);
                }

                // Format items to include product details
                const formattedItems = returnData.items?.map((item: any, index: number) => ({
                    ...item,
                    srNo: index + 1,
                    id: item.id || item._id,
                })) || [];

                // Format dates for react-hook-form
                const formattedData: ProductReturnFormData = {
                    productReturnNumber: returnData.productReturnNumber || '',
                    productReturnDate: returnData.productReturnDate ? new Date(returnData.productReturnDate) : new Date(),
                    in_date: returnData.in_date ? new Date(returnData.in_date) : undefined,
                    out_date: returnData.out_date ? new Date(returnData.out_date) : undefined,
                    items: formattedItems,
                    // Store only the ID, not the full object
                    party: returnData.party?._id || returnData.party || undefined,
                    vendor: returnData.vendor?._id || returnData.vendor || undefined,
                    description: returnData.description || '',
                    status: returnData.status || 'RETURN_CREATED',
                    previous_status: returnData.status,
                };

                reset(formattedData);
                setValue('previous_status', returnData.status);
            } else {
                // For new returns, only set non-return number fields
                setValue('productReturnDate', new Date());
                setValue('status', 'RETURN_CREATED');
                setValue('previous_status', 'RETURN_CREATED');
                setValue('items', []);

                // Fetch return number separately
                await fetchNextProductReturnNumber();
            }
        } catch (err: any) {
            console.error('Error fetching data:', err);
            setError(err.message || 'Failed to load data. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [id, isEditMode, reset, setValue, fetchNextProductReturnNumber]);

    // Fetch data on component mount
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Show status note field when status changes
    useEffect(() => {
        if (isEditMode && previousStatus && status !== previousStatus) {
            setShowStatusNote(true);
        } else {
            setShowStatusNote(false);
        }
    }, [status, previousStatus, isEditMode]);

    // Item management functions
    const handleProductSelect = (product: ProductType | null) => {
        setSelectedProduct(product);
        if (product) {
            setItemForm({
                srNo: items.length + 1,
                productId: product._id,
                itemName: product.name,
                aliasName: '',
                hsnNo: product.partNo || '',
                quantity: 1,
                price: 0,
                amount: 0,
            });
        }
    };

    const calculateItemAmount = (quantity: number = 0, price: number = 0) => {
        return quantity * price;
    };

    const handleItemFormChange = (field: keyof Item, value: any) => {
        setItemForm(prev => {
            const updated = { ...prev, [field]: value };

            // Recalculate amount when quantity or price changes
            if (field === 'quantity' || field === 'price') {
                const quantity = field === 'quantity' ? Number(value) : (prev.quantity || 0);
                const price = field === 'price' ? Number(value) : (prev.price || 0);
                updated.amount = calculateItemAmount(quantity, price);
            }

            return updated;
        });
    };

    const addItem = () => {
        if (!itemForm.productId) {
            toast.error('Please select a product');
            return;
        }

        if (!itemForm.quantity || itemForm.quantity <= 0) {
            toast.error('Please enter a valid quantity');
            return;
        }

        const currentItems = getValues('items') || [];
        const newItem: Item = {
            id: `temp-${Date.now()}`,
            srNo: currentItems.length + 1,
            productId: itemForm.productId,
            itemName: itemForm.itemName,
            aliasName: itemForm.aliasName,
            hsnNo: itemForm.hsnNo,
            quantity: Number(itemForm.quantity),
            price: Number(itemForm.price),
            amount: Number(itemForm.amount),
        };

        setValue('items', [...currentItems, newItem]);

        // Reset item form
        setSelectedProduct(null);
        setItemForm({
            srNo: currentItems.length + 2,
            itemName: '',
            aliasName: '',
            hsnNo: '',
            quantity: 1,
            price: 0,
            amount: 0,
            productId: ''
        });
    };

    const updateItem = (index: number) => {
        if (!itemForm.quantity || itemForm.quantity <= 0) {
            toast.error('Please enter a valid quantity');
            return;
        }

        const currentItems = getValues('items') || [];
        const updatedItem: Item = {
            ...currentItems[index],
            productId: itemForm.productId || currentItems[index].productId,
            itemName: itemForm.itemName || currentItems[index].itemName,
            aliasName: itemForm.aliasName,
            hsnNo: itemForm.hsnNo,
            quantity: Number(itemForm.quantity),
            price: Number(itemForm.price),
            amount: Number(itemForm.amount),
        };

        currentItems[index] = updatedItem;
        setValue('items', currentItems);
        setEditingItemIndex(null);
        setSelectedProduct(null);
        setItemForm({
            srNo: currentItems.length + 1,
            itemName: '',
            aliasName: '',
            hsnNo: '',
            quantity: 1,
            price: 0,
            amount: 0,
            productId: ''
        });
    };

    const deleteItem = (index: number) => {
        const currentItems = getValues('items') || [];
        const updatedItems = currentItems.filter((_, i) => i !== index);

        // Recalculate sr numbers
        updatedItems.forEach((item, idx) => {
            item.srNo = idx + 1;
        });

        setValue('items', updatedItems);

        if (editingItemIndex === index) {
            setEditingItemIndex(null);
            setSelectedProduct(null);
            setItemForm({
                srNo: updatedItems.length + 1,
                itemName: '',
                aliasName: '',
                hsnNo: '',
                quantity: 1,
                price: 0,
                amount: 0,
                productId: ''
            });
        }
    };

    const startEditing = (index: number) => {
        const item = items[index];
        const product = products.find(p => p._id === item.productId);

        setEditingItemIndex(index);
        setSelectedProduct(product || null);
        setItemForm({
            srNo: item.srNo,
            productId: item.productId,
            itemName: item.itemName,
            aliasName: item.aliasName || '',
            hsnNo: item.hsnNo || '',
            quantity: item.quantity || 1,
            price: item.price || 0,
            amount: item.amount || 0,
        });
    };

    const cancelEditing = () => {
        setEditingItemIndex(null);
        setSelectedProduct(null);
        setItemForm({
            srNo: items.length + 1,
            itemName: '',
            aliasName: '',
            hsnNo: '',
            quantity: 1,
            price: 0,
            amount: 0,
            productId: ''
        });
    };

    // Add status note separately
    const handleAddStatusNote = async () => {
        const currentStatus = getValues('status');
        const statusNote = getValues('status_note');

        if (!statusNote) {
            toast.error('Please enter a note before adding');
            return;
        }

        try {
            const res = await request({
                url: `/productReturn/add-status-note/${id}`,
                method: 'post',
                data: {
                    note: statusNote,
                    from_status: previousStatus,
                    to_status: currentStatus
                },
            });

            if (res.success) {
                toast.success('Status note added successfully!');
                setStatusNotes(prev => [...prev, {
                    previous_status: previousStatus,
                    current_status: currentStatus,
                    note: statusNote,
                    created_at: new Date()
                }]);
                setValue('status_note', '');
                setShowStatusNote(false);
            }
        } catch (err: any) {
            toast.error(err || 'Failed to add status note');
        }
    };

    const onSubmit = async (data: ProductReturnFormData) => {
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            // Validate items
            if (!data.items || data.items.length === 0) {
                toast.error('Please add at least one item to the return');
                setSaving(false);
                return;
            }

            const payload: any = {
                productReturnNumber: data.productReturnNumber,
                productReturnDate: data.productReturnDate ? format(new Date(data.productReturnDate), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
                in_date: data.in_date ? format(new Date(data.in_date), 'yyyy-MM-dd') : null,
                out_date: data.out_date ? format(new Date(data.out_date), 'yyyy-MM-dd') : null,
                items: data.items.map(item => ({
                    productId: item.productId,
                    itemName: item.itemName,
                    aliasName: item.aliasName,
                    hsnNo: item.hsnNo,
                    quantity: item.quantity,
                    price: item.price,
                    amount: item.amount,
                })),
                party: data.party || null,
                vendor: data.vendor || null,
                description: data.description || '',
                status: data.status,
                status_note: data.status_note || '',
            };

            // Remove null/empty values for cleaner payload
            Object.keys(payload).forEach(key => {
                if (payload[key] === null || payload[key] === '' ||
                    (Array.isArray(payload[key]) && payload[key].length === 0)) {
                    delete payload[key];
                }
            });

            const apiUrl = isEditMode ? `productReturn/update-product-return/${id}` : 'productReturn/create-product-return';
            const method = isEditMode ? 'put' : 'post';

            const res = await request({
                url: apiUrl,
                method: method,
                data: payload,
            });

            if (res.success) {
                toast.success(res.message || `Product return ${isEditMode ? 'updated' : 'created'} successfully!`);
                navigate(-1);
            } else {
                throw new Error(res.message || 'Failed to save product return');
            }

        } catch (err: any) {
            setError(err.message || 'Failed to save product return');
            toast.error(err || 'Failed to save product return');
        } finally {
            setSaving(false);
        }
    };

    // Helper functions - Update these to handle both string IDs and object references
    const getSelectedParty = useCallback((): ProductReturnParty | null => {
        if (!party) return null;

        // If party is a string ID
        if (typeof party === 'string') {
            return parties.find(p => p._id === party) || null;
        }

        // If party is an object with _id (populated from API)
        if (typeof party === 'object' && party !== null && '_id' in party) {
            // Try to find matching party in the parties array
            const foundParty = parties.find(p => p._id === (party as any)._id);
            return foundParty || null;
        }

        return null;
    }, [party, parties]);

    const getSelectedVendor = useCallback((): ProductReturnVendor | null => {
        if (!vendor) return null;

        // If vendor is a string ID
        if (typeof vendor === 'string') {
            return vendors.find(v => v._id === vendor) || null;
        }

        // If vendor is an object with _id (populated from API)
        if (typeof vendor === 'object' && vendor !== null && '_id' in vendor) {
            // Try to find matching vendor in the vendors array
            const foundVendor = vendors.find(v => v._id === (vendor as any)._id);
            return foundVendor || null;
        }

        return null;
    }, [vendor, vendors]);

    const selectedParty = getSelectedParty();
    const selectedVendor = getSelectedVendor();


    // Calculate totals
    const totalQuantity = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const totalAmount = items.reduce((sum, item) => sum + (item.amount || 0), 0);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                    Loading product return data...
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                <Brandcrumb />
            </Box>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box sx={{ mb: 3 }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 3,
                        p: 2,
                        backgroundColor: 'white',
                        borderRadius: 1,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box>
                                <Typography variant="h5" fontWeight="bold">
                                    {isEditMode ? 'Edit Product Return' : 'Create New Product Return'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.9 }}>
                                    {isEditMode ? 'Update product return details' : 'Fill in the details to create a new product return'}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Card elevation={2} sx={{ borderRadius: 1 }}>
                        <CardContent>
                            <form onSubmit={handleSubmit(onSubmit)}>
                                {/* Two Column Layout */}
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>

                                    {/* LEFT COLUMN - Basic Information */}
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{
                                            color: 'primary.main',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: 3,
                                            pb: 1,
                                            borderBottom: '2px solid',
                                            borderColor: 'primary.main'
                                        }}>
                                            <ReceiptIcon />
                                            Return Information
                                        </Typography>

                                        {/* Return Number and Date in one row */}
                                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Controller
                                                    name="productReturnNumber"
                                                    control={control}
                                                    rules={{ required: 'Return number is required' }}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            label="Return Number *"
                                                            error={!!errors.productReturnNumber}
                                                            helperText={errors.productReturnNumber?.message}
                                                            size="small"
                                                            disabled
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        {isLoadingProductReturnNumber && !isEditMode ? (
                                                                            <CircularProgress size={20} />
                                                                        ) : (
                                                                            <ReceiptIcon color="action" />
                                                                        )}
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Box>

                                            <Box sx={{ flex: 1 }}>
                                                <Controller
                                                    name="productReturnDate"
                                                    control={control}
                                                    rules={{ required: 'Return date is required' }}
                                                    render={({ field }) => (
                                                        <DatePicker
                                                            label="Return Date *"
                                                            value={field.value ? new Date(field.value) : null}
                                                            onChange={field.onChange}
                                                            slotProps={{
                                                                textField: {
                                                                    fullWidth: true,
                                                                    size: 'small',
                                                                    error: !!errors.productReturnDate,
                                                                    helperText: errors.productReturnDate?.message,
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Box>
                                        </Box>

                                        {/* Status */}
                                        <Box sx={{ mb: 2 }}>
                                            <Controller
                                                name="status"
                                                control={control}
                                                rules={{ required: 'Status is required' }}
                                                render={({ field }) => (
                                                    <FormControl fullWidth size="small" error={!!errors.status}>
                                                        <InputLabel>Status *</InputLabel>
                                                        <Select {...field} label="Status *">
                                                            {STATUS_OPTIONS.map((statusOption) => (
                                                                <MenuItem key={statusOption} value={statusOption}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <span>{STATUS_CONFIG[statusOption].icon}</span>
                                                                        {STATUS_CONFIG[statusOption].label}
                                                                    </Box>
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                        {errors.status && (
                                                            <FormHelperText>{errors.status.message}</FormHelperText>
                                                        )}
                                                    </FormControl>
                                                )}
                                            />
                                        </Box>

                                        {/* In Date and Out Date in one row */}
                                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Controller
                                                    name="in_date"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <DatePicker
                                                            label="In Date"
                                                            value={field.value ? new Date(field.value) : null}
                                                            onChange={field.onChange}
                                                            slotProps={{
                                                                textField: {
                                                                    fullWidth: true,
                                                                    size: 'small',
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Box>

                                            <Box sx={{ flex: 1 }}>
                                                <Controller
                                                    name="out_date"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <DatePicker
                                                            label="Out Date"
                                                            value={field.value ? new Date(field.value) : null}
                                                            onChange={field.onChange}
                                                            slotProps={{
                                                                textField: {
                                                                    fullWidth: true,
                                                                    size: 'small',
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Box>
                                        </Box>

                                        {/* Description */}
                                        <Controller
                                            name="description"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label="Description"
                                                    multiline
                                                    rows={3}
                                                    size="small"
                                                    placeholder="Add any additional details about this return..."
                                                    sx={{ mb: 2 }}
                                                />
                                            )}
                                        />

                                        {/* Status Note Field (Visible when status changes in edit mode or for new returns) */}
                                        <Collapse in={showStatusNote || !isEditMode}>
                                            <Box sx={{ mb: 3 }}>
                                                <Controller
                                                    name="status_note"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            label="Status Note"
                                                            placeholder="Add a note for this status change..."
                                                            multiline
                                                            rows={2}
                                                            size="small"
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <NoteAddIcon color="action" />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            helperText={isEditMode ? "Add a note explaining why the status was changed" : "Add a note for the initial status"}
                                                        />
                                                    )}
                                                />
                                                {isEditMode && showStatusNote && (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<NoteAddIcon />}
                                                        onClick={handleAddStatusNote}
                                                        sx={{ mt: 1 }}
                                                    >
                                                        Add Note Separately
                                                    </Button>
                                                )}
                                            </Box>
                                        </Collapse>

                                        {/* Show existing status notes */}
                                        {isEditMode && statusNotes.length > 0 && (
                                            <Box sx={{ mt: 3 }}>
                                                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <HistoryIcon fontSize="small" />
                                                    Status History Notes
                                                </Typography>
                                                <Stack spacing={1}>
                                                    {statusNotes.map((note, index) => (
                                                        <Paper key={index} sx={{ p: 1.5, bgcolor: 'background.default' }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                                <Box>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {note.previous_status
                                                                            ? `${STATUS_CONFIG[note.previous_status as Status]?.icon || '📝'} ${note.previous_status} → ${STATUS_CONFIG[note.current_status as Status]?.icon || '📝'} ${note.current_status}`
                                                                            : `${STATUS_CONFIG[note.current_status as Status]?.icon || '📝'} ${note.current_status}`}
                                                                    </Typography>
                                                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                                        {note.note}
                                                                    </Typography>
                                                                </Box>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {format(new Date(note.created_at), 'dd/MM/yyyy HH:mm')}
                                                                </Typography>
                                                            </Box>
                                                        </Paper>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* RIGHT COLUMN - Party, Vendor Information */}
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{
                                            color: 'primary.main',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: 3,
                                            pb: 1,
                                            borderBottom: '2px solid',
                                            borderColor: 'primary.main'
                                        }}>
                                            <BusinessIcon />
                                            Party & Vendor Information
                                        </Typography>

                                        {/* Party (Customer) Selection */}
                                        <Box sx={{ mb: 3 }}>
                                            <Controller
                                                name="party"
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControl fullWidth size="small">
                                                        <Autocomplete
                                                            size='small'
                                                            options={parties}
                                                            getOptionLabel={(option: ProductReturnParty) => option?.partyName || ''}
                                                            value={selectedParty}
                                                            isOptionEqualToValue={(option: ProductReturnParty, value: ProductReturnParty | null) => {
                                                                if (!option || !value) return false;
                                                                return option._id === value._id;
                                                            }}
                                                            onChange={(_, newValue: ProductReturnParty | null) => {
                                                                // Send only the ID to the form
                                                                field.onChange(newValue?._id || '');
                                                            }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Customer (Party)"
                                                                    placeholder="Search customer by name..."
                                                                    InputProps={{
                                                                        ...params.InputProps,
                                                                        startAdornment: (
                                                                            <>
                                                                                <InputAdornment position="start">
                                                                                    <PersonIcon color="action" />
                                                                                </InputAdornment>
                                                                                {params.InputProps.startAdornment}
                                                                            </>
                                                                        ),
                                                                    }}
                                                                />
                                                            )}
                                                            renderOption={(props, option: ProductReturnParty) => (
                                                                <li {...props}>
                                                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                                        <Typography variant="body2">{option.partyName}</Typography>
                                                                        {option.nickName && (
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                ({option.nickName})
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                </li>
                                                            )}
                                                        />
                                                        <FormHelperText>
                                                            Select the customer returning the product
                                                        </FormHelperText>
                                                    </FormControl>
                                                )}
                                            />
                                        </Box>

                                        {/* Vendor Selection */}
                                        <Box sx={{ mb: 3 }}>
                                            <Controller
                                                name="vendor"
                                                control={control}
                                                render={({ field }) => (
                                                    <FormControl fullWidth size="small">
                                                        <Autocomplete
                                                            size='small'
                                                            options={vendors}
                                                            getOptionLabel={(option: ProductReturnVendor) => option?.vendorName || ''}
                                                            value={selectedVendor}
                                                            isOptionEqualToValue={(option: ProductReturnVendor, value: ProductReturnVendor | null) => {
                                                                if (!option || !value) return false;
                                                                return option._id === value._id;
                                                            }}
                                                            onChange={(_, newValue: ProductReturnVendor | null) => {
                                                                // Send only the ID to the form
                                                                field.onChange(newValue?._id || '');
                                                            }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Vendor"
                                                                    placeholder="Search vendor..."
                                                                    InputProps={{
                                                                        ...params.InputProps,
                                                                        startAdornment: (
                                                                            <>
                                                                                <InputAdornment position="start">
                                                                                    <BusinessIcon color="action" />
                                                                                </InputAdornment>
                                                                                {params.InputProps.startAdornment}
                                                                            </>
                                                                        ),
                                                                    }}
                                                                />
                                                            )}
                                                            renderOption={(props, option: ProductReturnVendor) => (
                                                                <li {...props}>
                                                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                                        <Typography variant="body2">{option.vendorName}</Typography>
                                                                        {option.contact?.email?.[0] && (
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                {option.contact.email[0]}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                </li>
                                                            )}
                                                        />
                                                        <FormHelperText>
                                                            Select the vendor (if return is related to vendor)
                                                        </FormHelperText>
                                                    </FormControl>
                                                )}
                                            />
                                        </Box>

                                        {/* Summary Cards */}
                                        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                            <Paper sx={{ flex: 1, p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                                                <Typography variant="caption">Total Items</Typography>
                                                <Typography variant="h5">{items.length}</Typography>
                                            </Paper>
                                            <Paper sx={{ flex: 1, p: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                                                <Typography variant="caption">Total Quantity</Typography>
                                                <Typography variant="h5">{totalQuantity}</Typography>
                                            </Paper>
                                            <Paper sx={{ flex: 1, p: 2, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                                                <Typography variant="caption">Total Amount</Typography>
                                                <Typography variant="h5">₹{totalAmount.toFixed(2)}</Typography>
                                            </Paper>
                                        </Box>
                                    </Box>
                                </Box>

                                <Divider sx={{ my: 3 }} />

                                {/* PRODUCTS SECTION */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{
                                        color: 'primary.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mb: 3,
                                        pb: 1,
                                        borderBottom: '2px solid',
                                        borderColor: 'primary.main'
                                    }}>
                                        <InventoryIcon />
                                        Return Items
                                    </Typography>

                                    {/* Add Item Form */}
                                    <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'background.default', borderRadius: 1 }}>
                                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'flex-start' }}>
                                            {/* Product Selection */}
                                            <Box sx={{ flex: 2 }}>
                                                <Autocomplete
                                                    size='small'
                                                    options={products}
                                                    getOptionLabel={(option) => `${option.name} (${option.partNo})`}
                                                    value={selectedProduct}
                                                    onChange={(_, newValue) => handleProductSelect(newValue)}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Select Product"
                                                            placeholder="Search by name or part number..."
                                                            size="small"
                                                        />
                                                    )}
                                                    renderOption={(props, option) => (
                                                        <li {...props}>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                                <Typography variant="body2">{option.name}</Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Part No: {option.partNo}
                                                                </Typography>
                                                            </Box>
                                                        </li>
                                                    )}
                                                />
                                            </Box>

                                            {/* Quantity */}
                                            <Box sx={{ flex: 1 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Quantity"
                                                    type="number"
                                                    value={itemForm.quantity}
                                                    onChange={(e) => handleItemFormChange('quantity', e.target.value)}
                                                    InputProps={{ inputProps: { min: 1 } }}
                                                />
                                            </Box>

                                            {/* Price */}
                                            <Box sx={{ flex: 1 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Price"
                                                    type="number"
                                                    value={itemForm.price}
                                                    onChange={(e) => handleItemFormChange('price', e.target.value)}
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                        inputProps: { min: 0, step: 0.01 }
                                                    }}
                                                />
                                            </Box>

                                            {/* Amount (Read-only) */}
                                            <Box sx={{ flex: 1 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Amount"
                                                    type="number"
                                                    value={itemForm.amount?.toFixed(2) || '0.00'}
                                                    InputProps={{
                                                        readOnly: true,
                                                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                    }}
                                                />
                                            </Box>

                                            {/* Action Buttons */}
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                {editingItemIndex !== null ? (
                                                    <>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            startIcon={<SaveIcon />}
                                                            onClick={() => updateItem(editingItemIndex)}
                                                        >
                                                            Update
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={<CancelIcon />}
                                                            onClick={cancelEditing}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={<AddIcon />}
                                                        onClick={addItem}
                                                    >
                                                        Add Item
                                                    </Button>
                                                )}
                                            </Box>
                                        </Box>
                                    </Paper>

                                    {/* Items Table */}
                                    {items.length > 0 ? (
                                        <>
                                            <TableContainer component={Paper} variant="outlined">
                                                <Table size="small">
                                                    <TableHead sx={{ bgcolor: 'background.default' }}>
                                                        <TableRow>
                                                            <TableCell>Sr No</TableCell>
                                                            <TableCell>Product</TableCell>
                                                            <TableCell>HSN</TableCell>
                                                            <TableCell align="right">Quantity</TableCell>
                                                            <TableCell align="right">Price (₹)</TableCell>
                                                            <TableCell align="right">Amount (₹)</TableCell>
                                                            <TableCell align="center">Actions</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {items
                                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                            .map((item, index) => {
                                                                const actualIndex = page * rowsPerPage + index;
                                                                return (
                                                                    <TableRow key={item.id || index} hover>
                                                                        <TableCell>{item.srNo}</TableCell>
                                                                        <TableCell>
                                                                            <Typography variant="body2">{item.itemName}</Typography>
                                                                            {item.aliasName && (
                                                                                <Typography variant="caption" color="text.secondary">
                                                                                    {item.aliasName}
                                                                                </Typography>
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell>{item.hsnNo || '-'}</TableCell>
                                                                        <TableCell align="right">{item.quantity}</TableCell>
                                                                        <TableCell align="right">₹{item.price?.toFixed(2)}</TableCell>
                                                                        <TableCell align="right">₹{item.amount?.toFixed(2)}</TableCell>
                                                                        <TableCell align="center">
                                                                            <IconButton
                                                                                size="small"
                                                                                color="primary"
                                                                                onClick={() => startEditing(actualIndex)}
                                                                            >
                                                                                <EditIcon fontSize="small" />
                                                                            </IconButton>
                                                                            <IconButton
                                                                                size="small"
                                                                                color="error"
                                                                                onClick={() => deleteItem(actualIndex)}
                                                                            >
                                                                                <DeleteIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>

                                            {/* Pagination */}
                                            <TablePagination
                                                rowsPerPageOptions={[5, 10, 25]}
                                                component="div"
                                                count={items.length}
                                                rowsPerPage={rowsPerPage}
                                                page={page}
                                                onPageChange={(_, newPage) => setPage(newPage)}
                                                onRowsPerPageChange={(e) => {
                                                    setRowsPerPage(parseInt(e.target.value, 10));
                                                    setPage(0);
                                                }}
                                            />

                                            {/* Total Row */}
                                            <Paper sx={{ p: 2, mt: 2, bgcolor: 'background.default' }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
                                                    <Typography variant="subtitle2">
                                                        Total Items: <strong>{items.length}</strong>
                                                    </Typography>
                                                    <Typography variant="subtitle2">
                                                        Total Quantity: <strong>{totalQuantity}</strong>
                                                    </Typography>
                                                    <Typography variant="subtitle2">
                                                        Total Amount: <strong>₹{totalAmount.toFixed(2)}</strong>
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        </>
                                    ) : (
                                        <Alert severity="info" sx={{ mt: 2 }}>
                                            No items added yet. Please add products to this return.
                                        </Alert>
                                    )}
                                </Box>

                                <Divider sx={{ mb: 2 }} />

                                {/* Action Buttons */}
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: 2,
                                }}>
                                    <Button
                                        variant="outlined"
                                        size='small'
                                        color="inherit"
                                        onClick={() => navigate(-1)}
                                        disabled={saving}
                                        sx={{ minWidth: 120, px: 3 }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        size='small'
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        disabled={saving || items.length === 0 || isLoadingProductReturnNumber}
                                        sx={{ minWidth: 140, px: 4 }}
                                    >
                                        {saving ? (
                                            <CircularProgress size={24} color="inherit" />
                                        ) : (
                                            isEditMode ? 'Update Return' : 'Create Return'
                                        )}
                                    </Button>
                                </Box>
                            </form>
                        </CardContent>
                    </Card>
                </Box>

                
            </LocalizationProvider>
        </>
    );
};

export default CreateEditProductReturn;